import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateNote,
  EstateNoteType,
  EstateWorkItem,
  useEstateNote_DeleteEstateNoteMutation,
  useEstateNote_GetInProgressEstateNoteFromWorkItemQuery,
  useEstateNote_GetPublishedEstateNotesFromWorkItemQuery,
  useEstateNote_UpdateEstateNoteMutation,
} from 'queries';

import { Button } from 'common/button';
import PublishedEstateNotes from 'pages/common/estate/published-notes';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import 'react-quill/dist/quill.snow.css';

const useStyles = makeStyles((theme) => ({
  actionButtonsContainer: {
    marginLeft: 1,
    gap: 10,
    display: 'flex',
    marginTop: 10,
  },
  editorTitle: {
    padding: theme.spacing(0, 1.5),
    paddingTop: 10,
  },
}));

type Props = {
  estateWorkItem: EstateWorkItem;
};

const emptyQuillTaskNote = '<p><br></p>';
const modules = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }, 'link'],
  ],
};

const EstateWorkItemPublishedNotes = ({ estateWorkItem }: Props) => {
  const [noteText, setNoteText] = useState(emptyQuillTaskNote);
  const classes = useStyles();

  const { refetch: refetchNote, data: noteData } =
    useEstateNote_GetInProgressEstateNoteFromWorkItemQuery({
      variables: {
        workItemId: estateWorkItem.id,
        noteType: EstateNoteType.Published,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting in-progress estate note from work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  useEffect(() => {
    setNoteText(noteData?.data.note || emptyQuillTaskNote);
  }, [noteData?.data.note]);

  const { data: publishedEstateNotesData, refetch: refetchPublishedEstateNotes } =
    useEstateNote_GetPublishedEstateNotesFromWorkItemQuery({
      variables: {
        workItemId: estateWorkItem.id,
        portalId: estateWorkItem.estate?.portalId || '',
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting published estate notes from work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [saveNote, { loading: saveNoteExecuting }] = useEstateNote_UpdateEstateNoteMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate note: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handlePublishNoteClick = () => {
    saveNote({
      variables: {
        input: { id: noteData?.data.id || '', note: noteText, published: true },
      },
      onCompleted: () => {
        refetchNote();
        refetchPublishedEstateNotes();
      },
    });
  };

  const onSaveNoteClicked = () => {
    saveNote({
      variables: {
        input: { id: noteData?.data.id || '', note: noteText },
      },
    });
  };

  const [deleteNote] = useEstateNote_DeleteEstateNoteMutation({
    onError: (e) => {
      showAlert({
        message: 'Error deleting estate note: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const deleteNoteClickCallback = (estateNote: EstateNote) => {
    deleteNote({
      variables: {
        id: estateNote.id,
      },
      onCompleted: () => refetchPublishedEstateNotes(),
    });
  };

  return (
    <div>
      <div className={classes.editorTitle}>New Note</div>
      <ReactQuill theme="snow" value={noteText} onChange={setNoteText} modules={modules} />
      <Box className={classes.actionButtonsContainer}>
        <Button
          size="tiny"
          onClick={handlePublishNoteClick}
          disabled={noteText === emptyQuillTaskNote}
        >
          Publish
        </Button>

        <Button color="info" size="tiny" onClick={onSaveNoteClicked} disabled={saveNoteExecuting}>
          Save
        </Button>
      </Box>
      <PublishedEstateNotes
        deleteNoteClickCallback={deleteNoteClickCallback}
        notes={publishedEstateNotesData?.data}
        showDeleteLink
        title="Published Notes"
      />
    </div>
  );
};

export default EstateWorkItemPublishedNotes;
